import { pauseLiveData } from "@suse-o11y/live-data-gate";
import { useControlledState } from "@suse-o11y/use-controlled-state";
import { useUnmount } from "@suse-o11y/use-unmount";
import React from "react";

import { DialogProvider } from "./context";

const DialogRoot = React.lazy(async () => {
	const Dialog = await import("@radix-ui/react-dialog");

	return { default: Dialog.Root };
});

export type BoundaryProps = React.ComponentPropsWithoutRef<typeof DialogRoot>;

export const Boundary: React.FC<BoundaryProps> = ({ open, onOpenChange, children, ...props }) => {
	const [isOpen, setIsOpen] = useControlledState(open ?? false, onOpenChange);

	const onClose = React.useCallback(() => setIsOpen(false), [setIsOpen]);

	React.useEffect(() => {
		if (isOpen) {
			return pauseLiveData();
		}
	}, [isOpen]);

	useUnmount(() => {
		// make sure the unmounting closes the dialog
		onOpenChange?.(false);
	});

	return (
		<DialogProvider value={onClose}>
			<React.Suspense fallback={children}>
				<DialogRoot onOpenChange={setIsOpen} open={isOpen} {...props}>
					{children}
				</DialogRoot>
			</React.Suspense>
		</DialogProvider>
	);
};

Boundary.displayName = "Dialog.Boundary";
